/**
 * Every station setting is declared once here: the URL parameter that sets it,
 * where it is remembered, and how its value is read and written.
 *
 * Reading is pure so components can use it during render. Storing happens in
 * `captureStationSettings`, which the app runs on every navigation. That matters
 * because generation redirects to URLs that no longer carry these parameters.
 */

export interface StationSetting<T> {
  param: string;
  storageKey: string;
  /** undefined when the URL says nothing about this setting */
  fromUrl: (raw: string) => T | undefined;
  fromStorage: (stored: string | null) => T;
  /** null removes the stored value */
  toStorage: (value: T) => string | null;
}

const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string | null): void => {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // Ignore localStorage errors, such as private browsing
  }
};

const onOffFromUrl = (raw: string): boolean | undefined => {
  const value = raw.trim();
  if (value === 'true' || value === '1') return true;
  if (value === 'false' || value === '0') return false;
  return undefined;
};

/** Station mode itself. It has no off value, so it stays on once seen. */
export const STATION_MODE_SETTING: StationSetting<boolean> = {
  param: 'mode',
  storageKey: 'napster_station_mode',
  fromUrl: (raw) => (raw === 'ces' || raw === 'station' ? true : undefined),
  fromStorage: (stored) => stored === 'true',
  toStorage: (value) => (value ? 'true' : null),
};

export const COMMUNICATION_SETTING: StationSetting<string | null> = {
  param: 'communication',
  storageKey: 'napster_station_communication_mode',
  fromUrl: (raw) => (raw === 'websockets' ? 'websockets' : undefined),
  fromStorage: (stored) => stored,
  toStorage: (value) => value,
};

export const LOW_DATA_MODE_SETTING: StationSetting<boolean> = {
  param: 'lowdatamode',
  storageKey: 'napster_low_data_mode',
  fromUrl: onOffFromUrl,
  fromStorage: (stored) => stored === 'true',
  toStorage: (value) => (value ? 'true' : null),
};

/**
 * Off, on without a tag, or on with a conference tag. The stored value is the
 * tag itself, or "true" when the contest runs without one.
 */
export const CONTEST_SETTING: StationSetting<string | null> = {
  param: 'contest',
  storageKey: 'napster_station_contest',
  fromUrl: (raw) => {
    const value = raw.trim();
    if (value === '' || value === 'false' || value === '0') return null;
    if (value === 'true' || value === '1') return 'true';
    return value;
  },
  fromStorage: (stored) => stored,
  toStorage: (value) => value,
};

/** Cover printing and camera capture are on unless a parameter switched them off */
export const PRINT_SETTING: StationSetting<boolean> = {
  param: 'print',
  storageKey: 'napster_station_print',
  fromUrl: onOffFromUrl,
  fromStorage: (stored) => stored !== 'false',
  toStorage: (value) => (value ? null : 'false'),
};

export const PHOTO_SETTING: StationSetting<boolean> = {
  param: 'photo',
  storageKey: 'napster_station_photo',
  fromUrl: onOffFromUrl,
  fromStorage: (stored) => stored !== 'false',
  toStorage: (value) => (value ? null : 'false'),
};

export const STATION_SETTINGS = [
  STATION_MODE_SETTING,
  COMMUNICATION_SETTING,
  LOW_DATA_MODE_SETTING,
  CONTEST_SETTING,
  PRINT_SETTING,
  PHOTO_SETTING,
];

const listeners = new Set<() => void>();

export const subscribeToStationSettings = (
  listener: () => void,
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const notifyStationSettings = (): void => {
  listeners.forEach((listener) => listener());
};

const currentSearch = (): string =>
  typeof window === 'undefined' ? '' : window.location.search;

/** The value in force: the URL when it says something, otherwise what was stored */
export const resolveStationSetting = <T>(
  setting: StationSetting<T>,
  search?: string,
): T => {
  const raw = new URLSearchParams(search ?? currentSearch()).get(setting.param);
  const fromUrl = raw === null ? undefined : setting.fromUrl(raw);

  return fromUrl === undefined
    ? setting.fromStorage(readStorage(setting.storageKey))
    : fromUrl;
};

export const writeStationSetting = <T>(
  setting: StationSetting<T>,
  value: T,
): void => {
  writeStorage(setting.storageKey, setting.toStorage(value));
  notifyStationSettings();
};

/** Remembers whatever the URL states, leaving the rest untouched */
export const captureStationSetting = <T>(
  setting: StationSetting<T>,
  search?: string,
): void => {
  const raw = new URLSearchParams(search ?? currentSearch()).get(setting.param);
  if (raw === null) return;

  const fromUrl = setting.fromUrl(raw);
  if (fromUrl === undefined) return;

  const stored = setting.toStorage(fromUrl);
  if (stored === readStorage(setting.storageKey)) return;

  writeStorage(setting.storageKey, stored);
  notifyStationSettings();
};

export const captureStationSettings = (search?: string): void => {
  captureStationSetting(STATION_MODE_SETTING, search);
  captureStationSetting(COMMUNICATION_SETTING, search);
  captureStationSetting(LOW_DATA_MODE_SETTING, search);
  captureStationSetting(CONTEST_SETTING, search);
  captureStationSetting(PRINT_SETTING, search);
  captureStationSetting(PHOTO_SETTING, search);
};

/** Rebuilds the station URL from what is remembered, so a reload keeps every setting */
export const stationSettingsUrlParams = (): URLSearchParams => {
  const params = new URLSearchParams();

  if (resolveStationSetting(STATION_MODE_SETTING)) {
    params.set(STATION_MODE_SETTING.param, 'station');
  }
  if (resolveStationSetting(COMMUNICATION_SETTING) === 'websockets') {
    params.set(COMMUNICATION_SETTING.param, 'websockets');
  }
  if (resolveStationSetting(LOW_DATA_MODE_SETTING)) {
    params.set(LOW_DATA_MODE_SETTING.param, 'true');
  }

  const contest = resolveStationSetting(CONTEST_SETTING);
  if (contest) {
    params.set(CONTEST_SETTING.param, contest);
  }

  if (!resolveStationSetting(PRINT_SETTING)) {
    params.set(PRINT_SETTING.param, 'false');
  }
  if (!resolveStationSetting(PHOTO_SETTING)) {
    params.set(PHOTO_SETTING.param, 'false');
  }

  return params;
};
